import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import { BrandLogo, PrimaryCTA } from '../../components';
import { GoogleSignInButton } from '../../components/GoogleSignInButton';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { claimSharedItem, viewPublicShare } from './sharingSlice';
import { selectIsAuthenticated } from '../auth/authSlice';
import { RedirectManager } from '../../utils/storage';
import { fpColors } from '../../styles/theme';

type ShareClaimScreenRouteProp = RouteProp<
  { ShareClaim: { shareToken: string } },
  'ShareClaim'
>;

/**
 * Share claim landing — a redesigned ShareClaimScreen that mirrors the
 * Stitch `shared_item_access` locked-preview layout for unauthenticated
 * recipients, while preserving the full authenticated claim flow
 * (optional message, Claim/Decline actions) behind the same redux thunks.
 *
 * Redux wiring preserved:
 *   - viewPublicShare(shareToken) on mount
 *   - claimSharedItem({ shareToken, request }) on confirm
 *   - auth-aware navigation: stashes return URL + routes to Login if
 *     unauthenticated when the user taps Create Account / Log In.
 */
export const ShareClaimScreen: React.FC = () => {
  const route = useRoute<ShareClaimScreenRouteProp>();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const { shareToken } = route.params;

  const { publicShare, loading, error } = useAppSelector((s) => s.sharing);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const [claimMessage, setClaimMessage] = useState('');

  useEffect(() => {
    if (shareToken) dispatch(viewPublicShare(shareToken));
  }, [dispatch, shareToken]);

  // Stash the current share URL before any OAuth redirect so Google
  // sign-in returns the recipient to THIS claim page. The email CTAs
  // stash on press (stashReturnAndGo); the Google button redirects
  // immediately, so the return URL must already be stored when tapped.
  useEffect(() => {
    if (isAuthenticated) return;
    (async () => {
      try {
        const returnUrl = RedirectManager.getCurrentUrl();
        if (returnUrl) await RedirectManager.storeRedirectUrl(returnUrl);
      } catch {}
    })();
  }, [isAuthenticated]);

  const stashReturnAndGo = async (route: 'Login' | 'Register') => {
    try {
      const returnUrl = RedirectManager.getCurrentUrl();
      if (returnUrl) await RedirectManager.storeRedirectUrl(returnUrl);
    } catch {}
    (navigation as any).navigate(route);
  };

  const handleClaim = async () => {
    if (!publicShare) return;
    if (!isAuthenticated) {
      return stashReturnAndGo('Login');
    }
    try {
      await dispatch(
        claimSharedItem({
          shareToken,
          request: { message: claimMessage.trim() || undefined },
        }),
      ).unwrap();
      Alert.alert(
        'Claimed',
        'This item is now in your inventory. You can view it any time.',
      );
    } catch (e: any) {
      Alert.alert('Could not claim', e?.message || 'Please try again.');
    }
  };

  /** Decline / not now — auth-only. Closes the screen so the user
   *  drops back to where they came from (Dashboard, Share tab, or
   *  the notification inbox). No backend "decline share" endpoint
   *  exists yet (filed as BE follow-up), so this is purely a
   *  navigation action: the share row stays PENDING server-side,
   *  the recipient can come back to it any time via the
   *  notification inbox / email link. */
  const handleDecline = () => {
    Alert.alert(
      'Decline this share?',
      "You can always come back to it from your notification inbox if you change your mind.",
      [
        { text: 'Keep', style: 'cancel' },
        {
          text: 'Decline',
          style: 'destructive',
          onPress: () => {
            if ((navigation as any).canGoBack?.()) {
              (navigation as any).goBack();
            } else {
              (navigation as any).navigate('MainApp');
            }
          },
        },
      ],
    );
  };

  /** Back button — same dismissal behavior as Decline minus the
   *  confirmation prompt. Hidden when unauthenticated (the recipient
   *  arrived via email link with nowhere to "go back" to). */
  const handleBack = () => {
    if ((navigation as any).canGoBack?.()) {
      (navigation as any).goBack();
    } else {
      (navigation as any).navigate('MainApp');
    }
  };

  /* ——— Loading / error states ——— */

  if (loading.claiming && !publicShare) {
    return (
      <LockedShell onBack={isAuthenticated ? handleBack : undefined}>
        <View style={styles.stateWrap}>
          <ActivityIndicator color={fpColors.primary} />
          <Text style={styles.stateText}>Loading share…</Text>
        </View>
      </LockedShell>
    );
  }

  if (error && !publicShare) {
    return (
      <LockedShell onBack={isAuthenticated ? handleBack : undefined}>
        <View style={styles.stateWrap}>
          <MaterialIcons name="error-outline" size={36} color={fpColors.error} />
          <Text style={styles.stateTitleError}>Couldn&apos;t load this share</Text>
          <Text style={styles.stateText}>{error}</Text>
        </View>
      </LockedShell>
    );
  }

  if (!publicShare) {
    return (
      <LockedShell onBack={isAuthenticated ? handleBack : undefined}>
        <View style={styles.stateWrap}>
          <ActivityIndicator color={fpColors.primary} />
        </View>
      </LockedShell>
    );
  }

  const senderName = publicShare.owner?.name || 'Someone';
  const itemImage = publicShare.item?.image_url || null;
  const itemName = publicShare.item?.name || 'Shared item';

  /* ——— Unauthenticated = locked preview (Stitch shared_item_access) ——— */

  if (!isAuthenticated) {
    return (
      <LockedShell onBack={isAuthenticated ? handleBack : undefined}>
        <View style={styles.card}>
          <View style={styles.imageWrap}>
            {itemImage ? (
              <Image source={{ uri: itemImage }} style={styles.imageBlurred} blurRadius={14} />
            ) : (
              <View style={[styles.imageBlurred, styles.imagePlaceholder]} />
            )}
            <View style={styles.imageScrim} />
            <View style={styles.lockBadge}>
              <MaterialIcons name="lock" size={28} color="#fff" />
            </View>
            <View style={styles.itemTag}>
              <MaterialIcons name="inventory" size={14} color={fpColors.onPrimaryContainer} />
              <Text style={styles.itemTagText} numberOfLines={1}>
                {itemName}
              </Text>
            </View>
          </View>

          <View style={styles.body}>
            <Text style={styles.title}>Item shared with you</Text>
            <Text style={styles.subtitle}>
              Log in or create an account to securely access this shared
              item from <Text style={styles.subtitleBold}>{senderName}</Text>.
            </Text>

            <View style={{ height: 12 }} />

            {/* Google sign-in — fastest path for a recipient who landed
                here from an email link. Reuses the same button + OAuth as
                the crew-invite claim page (AcceptInviteScreen). The return
                URL is stashed on mount so the round-trip lands back here. */}
            <GoogleSignInButton label="Continue with Google" />

            <View style={{ height: 10 }} />

            <PrimaryCTA
              label="Create account"
              onPress={() => stashReturnAndGo('Register')}
              iconRight="arrow-forward"
            />
            <Pressable
              onPress={() => stashReturnAndGo('Login')}
              style={({ pressed }) => [
                styles.secondary,
                pressed && { opacity: 0.85 },
              ]}
            >
              <Text style={styles.secondaryLabel}>I already have an account</Text>
            </Pressable>

            <View style={styles.trustRow}>
              <MaterialIcons
                name="verified-user"
                size={14}
                color={fpColors.outline}
              />
              <Text style={styles.trustText}>Secure sharing via Founsi.ai</Text>
            </View>
          </View>
        </View>
      </LockedShell>
    );
  }

  /* ——— Authenticated = claim form ——— */

  const ownerInitials = senderName
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || 'U';

  // Parse the Access Level we stuffed into notes from ShareModal
  // (`[Access: View Only]` etc). If missing, fall back to sharing_type.
  const accessMatch = publicShare.notes?.match(/\[Access:\s*([^\]]+)\]/i);
  const accessLabel = accessMatch?.[1]?.trim();
  const cleanedNotes = publicShare.notes
    ?.replace(/\[Access:\s*[^\]]+\]/i, '')
    .trim();
  const permissionIcon: keyof typeof MaterialIcons.glyphMap =
    accessLabel?.toLowerCase() === 'view only'
      ? 'visibility'
      : accessLabel?.toLowerCase() === 'full access'
        ? 'admin-panel-settings'
        : publicShare.sharing_type === 'DONATE'
          ? 'card-giftcard'
          : 'volunteer-activism';
  const permissionLabel =
    accessLabel ||
    (publicShare.sharing_type === 'BORROW'
      ? 'Available to borrow'
      : 'Being given away');

  const category = publicShare.item?.category;
  const valueTxt =
    typeof publicShare.item?.estimated_value === 'number'
      ? `$${publicShare.item.estimated_value.toFixed(2)}`
      : null;

  const expiresIso = publicShare.expires_at;
  const expiresAt = expiresIso ? new Date(expiresIso) : null;

  return (
    <LockedShell onBack={isAuthenticated ? handleBack : undefined}>
      <View style={styles.card}>
        <View style={styles.imageWrap}>
          {itemImage ? (
            <Image source={{ uri: itemImage }} style={styles.imageFull} />
          ) : (
            <View style={[styles.imageFull, styles.imagePlaceholder]} />
          )}
          <View style={styles.heroBottomGradient} />
          <View style={styles.ownerPill}>
            <View style={styles.ownerPillAvatar}>
              <Text style={styles.ownerPillAvatarText}>{ownerInitials}</Text>
            </View>
            <View style={{ flexShrink: 1 }}>
              <Text style={styles.ownerPillKicker}>Shared by</Text>
              <Text style={styles.ownerPillName} numberOfLines={1}>
                {senderName}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.body}>
          <View style={styles.titleBlock}>
            <Text style={styles.kicker}>Item shared with you</Text>
            <Text style={styles.titleClaim}>{itemName}</Text>
            <View style={styles.metaRow}>
              {category ? (
                <View style={styles.metaPill}>
                  <Text style={styles.metaPillText}>{category}</Text>
                </View>
              ) : null}
              {valueTxt ? (
                <View style={[styles.metaPill, styles.metaPillValue]}>
                  <Text
                    style={[
                      styles.metaPillText,
                      { color: fpColors.tertiary },
                    ]}
                  >
                    {valueTxt}
                  </Text>
                </View>
              ) : null}
            </View>
          </View>

          <View style={styles.accessCard}>
            <View style={styles.accessIconWrap}>
              <MaterialIcons
                name={permissionIcon}
                size={22}
                color={fpColors.primary}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.accessKicker}>Access level</Text>
              <Text style={styles.accessLabel}>{permissionLabel}</Text>
              {publicShare.expected_return_date ? (
                <Text style={styles.accessMeta}>
                  Return by{' '}
                  {new Date(
                    publicShare.expected_return_date,
                  ).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Text>
              ) : null}
            </View>
          </View>

          {!!publicShare.item?.description && (
            <Text style={styles.subtitle} numberOfLines={6}>
              {publicShare.item.description}
            </Text>
          )}

          {!!cleanedNotes && (
            <View style={styles.noteBox}>
              <MaterialIcons
                name="format-quote"
                size={18}
                color={fpColors.primary}
              />
              <Text style={styles.noteText}>{cleanedNotes}</Text>
            </View>
          )}

          <TextInput
            value={claimMessage}
            onChangeText={setClaimMessage}
            placeholder="Add a message for the sender (optional)"
            placeholderTextColor={`${fpColors.outline}99`}
            style={styles.messageInput}
            multiline
          />

          <PrimaryCTA
            label={
              publicShare.is_claimed
                ? 'Already claimed'
                : publicShare.sharing_type === 'BORROW'
                  ? 'Accept & borrow'
                  : 'Accept item'
            }
            onPress={handleClaim}
            loading={loading.claiming}
            disabled={publicShare.is_claimed || !publicShare.can_claim}
            iconRight="check-circle"
          />

          {/* Decline — auth-only (an email-link visitor declining
              would not affect the share's server state, so we
              hide the button entirely until they sign in). */}
          {isAuthenticated && !publicShare.is_claimed && (
            <Pressable
              onPress={handleDecline}
              style={({ pressed }) => [
                styles.declineBtn,
                pressed && { opacity: 0.85 },
              ]}
              testID="share-claim-decline-btn"
              accessibilityRole="button"
              accessibilityLabel="Decline this share"
            >
              <Text style={styles.declineLabel}>Decline</Text>
            </Pressable>
          )}

          <View style={styles.trustRow}>
            <MaterialIcons
              name="verified-user"
              size={14}
              color={fpColors.outline}
            />
            <Text style={styles.trustText}>
              Secure sharing via Founsi.ai
              {expiresAt ? ` · Expires ${expiresAt.toLocaleDateString()}` : ''}
            </Text>
          </View>
        </View>
      </View>
    </LockedShell>
  );
};

/* ———————————— Shell ———————————— */

/** Top-bar wrapper for the claim screen. The back button is OPTIONAL
 *  — only an authenticated recipient gets one (an email-link visitor
 *  has nowhere to navigate back to within the app). */
const LockedShell: React.FC<{
  children: React.ReactNode;
  onBack?: () => void;
}> = ({ children, onBack }) => (
  <View style={styles.page}>
    <View pointerEvents="none" style={[styles.blur, styles.blurTopLeft]} />
    <View pointerEvents="none" style={[styles.blur, styles.blurBottomRight]} />
    <View style={styles.topBar}>
      <View style={styles.topBarInner}>
        {onBack ? (
          <Pressable
            onPress={onBack}
            style={styles.backBtn}
            hitSlop={8}
            testID="share-claim-back-btn"
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <MaterialIcons
              name="arrow-back"
              size={22}
              color={fpColors.onSurface}
            />
          </Pressable>
        ) : (
          // Placeholder spacer so the brand logo stays visually
          // centered whether or not the back button is present.
          <View style={{ width: 36 }} />
        )}
        <View style={{ flex: 1, alignItems: 'center' }}>
          <BrandLogo variant="horizontal" height={22} tone="color" />
        </View>
        <View style={{ width: 36 }} />
      </View>
    </View>
    <ScrollView
      contentContainerStyle={styles.scroll}
      showsVerticalScrollIndicator={false}
    >
      {children}
    </ScrollView>
  </View>
);

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  scroll: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  stateWrap: {
    alignItems: 'center',
    gap: 10,
    padding: 28,
  },
  stateText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
  stateTitleError: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.error,
    marginTop: 4,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 18,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.08,
    shadowRadius: 30,
    elevation: 6,
  },
  imageWrap: {
    position: 'relative',
    width: '100%',
    aspectRatio: 4 / 3,
    backgroundColor: fpColors.surfaceContainer,
  },
  imageFull: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  imageBlurred: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  imagePlaceholder: { backgroundColor: fpColors.primaryContainer },
  imageScrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(31,26,34,0.45)',
  },
  lockBadge: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 64,
    height: 64,
    marginLeft: -32,
    marginTop: -32,
    borderRadius: 32,
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.35)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemTag: {
    position: 'absolute',
    left: 14,
    bottom: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  heroBottomGradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '55%',
    backgroundColor: 'rgba(31,26,34,0.55)',
  },
  ownerPill: {
    position: 'absolute',
    left: 14,
    bottom: 14,
    right: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
    ...(Platform.OS === 'web'
      ? ({ backdropFilter: 'blur(8px)' } as any)
      : {}),
  },
  ownerPillAvatar: {
    width: 34,
    height: 34,
    borderRadius: 17,
    backgroundColor: '#fff',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ownerPillAvatarText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.primary,
    letterSpacing: 0.5,
  },
  ownerPillKicker: {
    fontFamily: 'Inter_500Medium',
    fontSize: 10,
    color: 'rgba(255,255,255,0.8)',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  ownerPillName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: '#fff',
  },
  titleBlock: { gap: 4 },
  kicker: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  titleClaim: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 24,
    color: fpColors.onBackground,
    letterSpacing: -0.4,
    marginTop: 2,
  },
  metaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 6,
  },
  metaPill: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  metaPillValue: {
    backgroundColor: `${fpColors.tertiary}14`,
    borderColor: `${fpColors.tertiary}33`,
  },
  metaPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    letterSpacing: 0.2,
  },
  accessCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 14,
    backgroundColor: `${fpColors.primary}0f`,
    borderWidth: 1,
    borderColor: `${fpColors.primary}2a`,
  },
  accessIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    backgroundColor: fpColors.surfaceContainerLowest,
    justifyContent: 'center',
    alignItems: 'center',
  },
  accessKicker: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  accessLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.onSurface,
    marginTop: 1,
  },
  accessMeta: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  itemTagText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 12,
    color: '#fff',
    maxWidth: 200,
  },
  body: { padding: 20, gap: 10 },
  title: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 22,
    color: fpColors.onBackground,
    letterSpacing: -0.3,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    lineHeight: 20,
    color: fpColors.onSurfaceVariant,
  },
  subtitleBold: { fontFamily: 'Inter_700Bold', color: fpColors.onSurface },

  senderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  senderAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  senderText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  senderName: {
    fontFamily: 'Inter_700Bold',
    color: fpColors.onSurface,
  },

  noteBox: {
    flexDirection: 'row',
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}0d`,
    borderLeftWidth: 3,
    borderLeftColor: fpColors.primary,
  },
  noteText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurface,
    fontStyle: 'italic',
  },

  typeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  typeLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.primary,
  },

  messageInput: {
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: 72,
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: fpColors.onSurface,
    textAlignVertical: 'top',
    ...(({ outlineStyle: 'none', outlineWidth: 0 } as any)),
  },

  secondary: {
    paddingVertical: 14,
    alignItems: 'center',
  },
  secondaryLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.primary,
  },

  // Back button — only rendered for authenticated visitors.
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLow,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Decline button — outlined, sits below the primary Accept CTA.
  declineBtn: {
    marginTop: 10,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outline,
    alignItems: 'center',
    justifyContent: 'center',
  },
  declineLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
  },

  trustRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 4,
  },
  trustText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.outline,
  },

  blur: {
    position: 'absolute',
    width: 256,
    height: 256,
    borderRadius: 128,
    opacity: 0.25,
  },
  blurTopLeft: {
    top: 80,
    left: -80,
    backgroundColor: fpColors.primary,
  },
  blurBottomRight: {
    bottom: 80,
    right: -80,
    backgroundColor: fpColors.tertiaryContainer,
  },
});
